// src/data/roddoRadioTracks.ts

export type RoddoRadioMoodId =
  | 'album-1'
  | 'album-2';

export interface RoddoRadioMood {
  id: RoddoRadioMoodId;
  label: string;
  description: string;
}

const audioSources = {
  'berry-pickin.mp3': new URL('../assets/audio/roddo-radio/berry-pickin.mp3', import.meta.url).href,
  'calm-waters-big-fish.mp3': new URL('../assets/audio/roddo-radio/calm-waters-big-fish.mp3', import.meta.url).href,
  'chasing-the-next-bend-2.mp3': new URL('../assets/audio/roddo-radio/chasing-the-next-bend-2.mp3', import.meta.url).href,
  'chasing-the-next-bend.mp3': new URL('../assets/audio/roddo-radio/chasing-the-next-bend.mp3', import.meta.url).href,
  'sea-foam.mp3': new URL('../assets/audio/roddo-radio/sea-foam.mp3', import.meta.url).href,
  'cheers-to-fishing.mp3': new URL('../assets/audio/roddo-radio/cheers-to-fishing.mp3', import.meta.url).href,
  'creekside-tension.mp3': new URL('../assets/audio/roddo-radio/creekside-tension.mp3', import.meta.url).href,
  'dawns-dew.mp3': new URL('../assets/audio/roddo-radio/dawns-dew.mp3', import.meta.url).href,
  'glass-water-dawn.mp3': new URL('../assets/audio/roddo-radio/glass-water-dawn.mp3', import.meta.url).href,
  'high-tide-window.mp3': new URL('../assets/audio/roddo-radio/high-tide-window.mp3', import.meta.url).href,
  'last-cast-before-dark.mp3': new URL('../assets/audio/roddo-radio/last-cast-before-dark.mp3', import.meta.url).href,
  'last-cast-glow.mp3': new URL('../assets/audio/roddo-radio/last-cast-glow.mp3', import.meta.url).href,
  'morning-myst.mp3': new URL('../assets/audio/roddo-radio/morning-myst.mp3', import.meta.url).href,
  'pondering-fish.mp3': new URL('../assets/audio/roddo-radio/pondering-fish.mp3', import.meta.url).href,
  'river-road-home.mp3': new URL('../assets/audio/roddo-radio/river-road-home.mp3', import.meta.url).href,
  'riverbed-turnaround.mp3': new URL('../assets/audio/roddo-radio/riverbed-turnaround.mp3', import.meta.url).href,
  'strike-zone.mp3': new URL('../assets/audio/roddo-radio/strike-zone.mp3', import.meta.url).href,
  'summers-evening-breeze.mp3': new URL('../assets/audio/roddo-radio/summers-evening-breeze.mp3', import.meta.url).href,
  'suns-rising-glow.mp3': new URL('../assets/audio/roddo-radio/suns-rising-glow.mp3', import.meta.url).href,
  'the-little-fish-that-could.mp3': new URL('../assets/audio/roddo-radio/the-little-fish-that-could.mp3', import.meta.url).href,
  'the-old-fishing-spot.mp3': new URL('../assets/audio/roddo-radio/the-old-fishing-spot.mp3', import.meta.url).href,
  'tide-loop.mp3': new URL('../assets/audio/roddo-radio/tide-loop.mp3', import.meta.url).href,
  'weedside-tension.mp3': new URL('../assets/audio/roddo-radio/weedside-tension.mp3', import.meta.url).href,
} as const;

type RoddoRadioFileName = keyof typeof audioSources;

export interface RoddoRadioTrack {
  id: string;
  title: string;
  artist: string;
  moodId: RoddoRadioMoodId;
  moodLabel: string;
  fileName: RoddoRadioFileName;
  src: string;
}

const radioAsset = (fileName: RoddoRadioFileName) => audioSources[fileName];

export const roddoRadioMoods: RoddoRadioMood[] = [
  {
    id: 'album-1',
    label: 'Album 1',
    description: 'The newest Roddo Radio batch: calm mornings, playful fish stories, and easy water vibes.',
  },
  {
    id: 'album-2',
    label: 'Album 2',
    description: 'The original Roddo Radio collection: road songs, crew days, calm logs, and strike-window tracks.',
  },
];

const moodLabelById = roddoRadioMoods.reduce<Record<RoddoRadioMoodId, string>>(
  (acc, mood) => {
    acc[mood.id] = mood.label;
    return acc;
  },
  {} as Record<RoddoRadioMoodId, string>,
);

const makeTrack = (
  id: string,
  title: string,
  moodId: RoddoRadioMoodId,
  fileName: RoddoRadioFileName,
): RoddoRadioTrack => ({
  id,
  title,
  artist: 'Roddo Radio',
  moodId,
  moodLabel: moodLabelById[moodId],
  fileName,
  src: radioAsset(fileName),
});

export const roddoRadioTracks: RoddoRadioTrack[] = [
  makeTrack('the-little-fish-that-could', 'The Little Fish That Could', 'album-1', 'the-little-fish-that-could.mp3'),
  makeTrack('calm-waters-big-fish', 'Calm Waters Big Fish', 'album-1', 'calm-waters-big-fish.mp3'),
  makeTrack('suns-rising-glow', 'Suns Rising Glow', 'album-1', 'suns-rising-glow.mp3'),
  makeTrack('summers-evening-breeze', 'Summers Evening Breeze', 'album-1', 'summers-evening-breeze.mp3'),
  makeTrack('dawns-dew', 'Dawns Dew', 'album-1', 'dawns-dew.mp3'),
  makeTrack('berry-pickin', 'Berry Pickin', 'album-1', 'berry-pickin.mp3'),
  makeTrack('mornings-myst', 'Morning Myst', 'album-1', 'morning-myst.mp3'),
  makeTrack('pondering-fish', 'Pondering Fish', 'album-1', 'pondering-fish.mp3'),
  makeTrack('tide-loop', 'Tide Loop', 'album-1', 'tide-loop.mp3'),

  makeTrack('glass-water-dawn', 'Glass Water Dawn', 'album-2', 'glass-water-dawn.mp3'),
  makeTrack('the-old-fishing-spot', 'The Old Fishing Spot', 'album-2', 'the-old-fishing-spot.mp3'),
  makeTrack('last-cast-before-dark', 'Last Cast Before Dark', 'album-2', 'last-cast-before-dark.mp3'),
  makeTrack('last-cast-glow', 'Last Cast Glow', 'album-2', 'last-cast-glow.mp3'),
  makeTrack('chasing-the-next-bend-2', 'Chasing The Next Bend 2.0', 'album-2', 'chasing-the-next-bend-2.mp3'),
  makeTrack('chasing-the-next-bend', 'Chasing The Next Bend', 'album-2', 'chasing-the-next-bend.mp3'),
  makeTrack('river-road-home', 'River Road Home', 'album-2', 'river-road-home.mp3'),
  makeTrack('riverbed-turnaround', 'Riverbed Turnaround', 'album-2', 'riverbed-turnaround.mp3'),
  makeTrack('cheers-to-fishing-2', 'Cheers to Fishing', 'album-2', 'cheers-to-fishing.mp3'),
  makeTrack('cheers-to-fishing', 'Sea Foam', 'album-2', 'sea-foam.mp3'),
  makeTrack('strike-zone', 'Strike Zone', 'album-2', 'strike-zone.mp3'),
  makeTrack('high-tide-window', 'High Tide Window', 'album-2', 'high-tide-window.mp3'),
  makeTrack('creekside-tension', 'Creekside Tension', 'album-2', 'creekside-tension.mp3'),
  makeTrack('weedside-tension', 'Weedside Tension', 'album-2', 'weedside-tension.mp3'),
];
